<template>
  <!-- 全局控制面板：常显，可拖动到任意位置 -->
  <div
    ref="panelRef"
    class="qk-panel"
    :class="{ dragging }"
    :style="panelStyle"
    @pointerdown="onDragStart"
    @pointermove="onDragMove"
    @pointerup="onDragEnd"
    @pointercancel="onDragEnd"
  >
    <!-- 顶部拖拽条：整宽可拖，双击复位 -->
    <div class="qk-dragbar" title="按住拖动，双击复位" @dblclick="resetPos"></div>

    <!-- 没有可用房间时，开关/门槛/保存都没有意义，直接锁定 -->
    <div v-if="!hasRooms" class="qk-empty">无可用房间，相关功能已锁定</div>

    <div v-else class="qk-bar">
      <!-- 杀开关 -->
      <div class="qk-kill">
        <span class="qk-label" title="全部房间的杀猪总开关">总控</span>
        <NSwitch
          :value="draftEnabled"
          size="small"
          :disabled="saving"
          :title="draftEnabled ? '关闭全部房间' : '开启全部房间'"
          @update:value="onEnabledChange"
        />
      </div>

      <!-- 参与门槛 + 历史金额下拉 -->
      <div class="qk-amount">
        <span class="qk-label">门槛</span>
        <div class="qk-input-wrap" @focusin="onAmountFocusIn" @focusout="onAmountFocusOut">
          <NInputNumber
            :value="draftAmount"
            class="qk-input"
            :min="0"
            :show-button="false"
            size="small"
            placeholder="0"
            @update:value="onAmountChange"
          />

          <!-- 历史金额：默认在输入框上方，拖到屏幕顶部时改为向下展开 -->
          <div
            v-if="showAmountDrop"
            class="qk-drop"
            :class="{ below: dropBelow }"
            @pointerdown.prevent
            @mousedown.prevent
          >
            <span
              v-for="p in amountOptions"
              :key="p"
              class="qk-drop-item"
              :class="{ active: normalizedAmount === p }"
              @click="pickAmount(p)"
            >{{ p }}</span>
          </div>
        </div>
      </div>

      <NButton
        size="small"
        :type="hasChanges ? 'primary' : 'success'"
        :loading="saving"
        :title="hasChanges ? '有未保存的变更，点击保存' : '当前已保存，点击可再次提交'"
        @click="handleSave"
      >
        <span class="qk-save-inner">
          <span v-if="hasChanges" class="qk-save-pulse"></span>
          <span v-else class="qk-check">✓</span>
          {{ hasChanges ? '有变更' : '已保存' }}
        </span>
      </NButton>
    </div>

    <!-- 锚点状态：嵌入在总控面板内（跟随玩家所在房间；点击展开完整锚点画面） -->
    <div v-if="hasRooms" class="qk-anchor">
      <AnchorFloatPanel embedded :room="room" :room-name="roomName" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { NButton, NInputNumber, NSwitch, createDiscreteApi } from 'naive-ui';
import AnchorFloatPanel from '@/components/AnchorFloatPanel.vue';

const props = defineProps<{
  /** 当前杀开关状态（= 是否选中全部房间） */
  enabled: boolean;
  /** 当前参与门槛金额 */
  amount: number;
  /** 可用房间数（0 表示状态可能还没拉到，会触发一次补拉） */
  roomCount: number;
  /** 保存回调：由父组件统一执行 SignalR 保存与状态同步 */
  onSave: (payload: { enabled: boolean; amount: number }) => Promise<boolean>;
  /** 可选：挂载时若房间数为 0（连接未就绪），让父组件补拉一次 */
  onRefresh?: () => void;
  /** 玩家当前所在房间（锚点状态跟随它） */
  room?: string;
  /** 房间展示名 */
  roomName?: string;
}>();

const api = createDiscreteApi(['message']);

/** 历史金额（保存过的门槛金额）存放处 */
const AMOUNT_KEY = 'kp_kill_amount_presets';
const MAX_AMOUNTS = 10;
/** 面板位置存放处 */
const POS_KEY = 'kp_quick_panel_pos';
/** 距离视口边缘的最小留白 */
const EDGE = 8;

const panelRef = ref<HTMLElement | null>(null);
/** null = 默认右下角（走 CSS），拖动后改为 left/top 绝对定位 */
const pos = ref<{ left: number; top: number } | null>(null);
const dragging = ref(false);

const saving = ref(false);
const draftEnabled = ref(props.enabled);
const draftAmount = ref<number | null>(props.amount ?? 0);

/** 用户手动改过开关/金额后，不再被外部同步覆盖 */
let enabledDirty = false;
let amountDirty = false;

/** 保存过的金额（下拉候选，升序展示） */
const amountOptions = ref<number[]>([]);
/** 输入框是否聚焦：聚焦且金额有效时，上方浮出历史金额 */
const amountFocused = ref(false);
let blurTimer: number | null = null;

const normalizedAmount = computed(() => {
  const v = draftAmount.value;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return Math.round(v * 100) / 100;
});

/** 输入框里是正常金额（非空、非负）才展示下拉 */
const hasValidAmount = computed(
  () => typeof draftAmount.value === 'number' && Number.isFinite(draftAmount.value) && draftAmount.value >= 0
);

const showAmountDrop = computed(
  () => amountFocused.value && hasValidAmount.value && amountOptions.value.length > 0
);

/** 服务端当前的门槛金额（保存基线，与面板草稿同精度） */
const baselineAmount = computed(() => {
  const v = props.amount;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return Math.round(v * 100) / 100;
});

/** 是否有未保存的变更：开关或门槛金额任一与基线不同 */
const hasChanges = computed(
  () => draftEnabled.value !== props.enabled || normalizedAmount.value !== baselineAmount.value
);

/** 是否有可用房间：没有房间就没有可控制对象，面板只显示锁定提示 */
const hasRooms = computed(() => props.roomCount > 0);

/** 面板贴近屏幕顶部时，下拉改为向下展开（否则会被视口裁掉） */
const dropBelow = computed(() => (pos.value ? pos.value.top < 170 : false));

const panelStyle = computed(() =>
  pos.value
    ? {
        left: `${pos.value.left}px`,
        top: `${pos.value.top}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : {}
);

// 服务端状态回来后同步到面板（用户正在编辑的字段不覆盖）
watch(
  () => props.enabled,
  (v) => {
    if (!enabledDirty) draftEnabled.value = v;
  }
);
watch(
  () => props.amount,
  (v) => {
    if (!amountDirty) draftAmount.value = v ?? 0;
  }
);

// ---------- 拖拽 ----------
interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
}

let dragState: DragState | null = null;
/** 本次按下是否真的移动过（避免“点一下”也把定位方式切走） */
let dragMoved = false;

function clampPosition(left: number, top: number) {
  const el = panelRef.value;
  const width = el?.offsetWidth ?? 320;
  const height = el?.offsetHeight ?? 44;
  const maxLeft = Math.max(EDGE, window.innerWidth - width - EDGE);
  const maxTop = Math.max(EDGE, window.innerHeight - height - EDGE);
  return {
    left: Math.min(Math.max(EDGE, left), maxLeft),
    top: Math.min(Math.max(EDGE, top), maxTop),
  };
}

/** 面板内的可交互控件不参与拖拽（.af-mini 是嵌入的锚点小地图；.af-modal 是展开的大面板，点它应关闭而非拖拽） */
const NO_DRAG_SELECTOR = 'input, textarea, select, button, a, .n-switch, .n-input, .n-button, .n-input-number, .qk-drop, .af-mini, .af-modal';

function onDragStart(e: PointerEvent) {
  if (e.button !== 0) return;
  const el = panelRef.value;
  if (!el) return;

  // 拖到开关/输入框/按钮上时交给控件自己处理
  const target = e.target as HTMLElement | null;
  if (target && target.closest(NO_DRAG_SELECTOR)) return;

  const rect = el.getBoundingClientRect();
  dragState = {
    pointerId: e.pointerId,
    startX: e.clientX,
    startY: e.clientY,
    originLeft: rect.left,
    originTop: rect.top,
  };
  dragMoved = false;
  dragging.value = true;

  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
}

function onDragMove(e: PointerEvent) {
  if (!dragState || e.pointerId !== dragState.pointerId) return;
  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;
  if (!dragMoved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
  dragMoved = true;
  pos.value = clampPosition(dragState.originLeft + dx, dragState.originTop + dy);
}

function onDragEnd(e: PointerEvent) {
  if (!dragState || e.pointerId !== dragState.pointerId) return;
  const target = e.currentTarget as HTMLElement;
  if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);

  dragState = null;
  dragging.value = false;

  if (dragMoved && pos.value) {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos.value));
    } catch (err) {
      // 存不下就只在本次会话生效
    }
  }
}

function readSavedPos(): { left: number; top: number } | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return { left: parsed.left, top: parsed.top };
    }
  } catch (e) {
    // 忽略损坏的配置
  }
  return null;
}

function onWindowResize() {
  if (pos.value) pos.value = clampPosition(pos.value.left, pos.value.top);
}

/** 双击把手回到默认的右下角 */
function resetPos() {
  pos.value = null;
  try {
    localStorage.removeItem(POS_KEY);
  } catch (e) {
    // 忽略
  }
}

// ---------- 历史金额 ----------
function loadAmountOptions() {
  try {
    const raw = localStorage.getItem(AMOUNT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    amountOptions.value = Array.isArray(arr)
      ? arr
          .filter((x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= 0)
          .map((x: number) => Math.round(x * 100) / 100)
          .slice(0, MAX_AMOUNTS)
          .sort((a, b) => a - b)
      : [];
  } catch (e) {
    amountOptions.value = [];
  }
}

function persistAmountOptions() {
  try {
    localStorage.setItem(AMOUNT_KEY, JSON.stringify(amountOptions.value));
  } catch (e) {
    // localStorage 不可用时忽略，候选只在本次会话生效
  }
}

/** 保存成功后把金额记入下拉候选：去重、只保留最近 10 个、升序展示 */
function rememberAmount(value: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return;
  amountOptions.value = [value, ...amountOptions.value.filter((x) => x !== value)]
    .slice(0, MAX_AMOUNTS)
    .sort((a, b) => a - b);
  persistAmountOptions();
}

function onEnabledChange(v: boolean) {
  draftEnabled.value = v;
  enabledDirty = true;
}

function onAmountChange(v: number | null) {
  // 允许输入过程中清空，但负数一律按 0 处理（最低 0）
  draftAmount.value = v === null ? null : Math.max(0, v);
  amountDirty = true;
}

/** 失焦/保存时收敛草稿：空值、非法值、负数一律落成 0，不存空 */
function normalizeAmountDraft() {
  const v = draftAmount.value;
  const next = typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  if (draftAmount.value !== next) {
    draftAmount.value = next;
    amountDirty = true;
  }
}

function pickAmount(value: number) {
  draftAmount.value = value;
  amountDirty = true;
}

function clearBlurTimer() {
  if (blurTimer) {
    clearTimeout(blurTimer);
    blurTimer = null;
  }
}

function onAmountFocusIn() {
  clearBlurTimer();
  amountFocused.value = true;
}

function onAmountFocusOut() {
  // 离开输入框就收敛：空 / 负数一律变 0，输入框里不留空
  normalizeAmountDraft();

  // 延迟收起：点击下拉项时 mousedown 已被阻止，这里只兜底输入框真失焦
  clearBlurTimer();
  blurTimer = window.setTimeout(() => {
    amountFocused.value = false;
    blurTimer = null;
  }, 120);
}

async function handleSave() {
  if (saving.value) return;
  // 注意：无变更时也允许保存（按钮此时显示“已保存”，点击等于强制再提交一次）
  saving.value = true;
  try {
    // 兜底：即便没走失焦流程，空值也按 0 提交
    normalizeAmountDraft();
    const amount = normalizedAmount.value;
    const ok = await props.onSave({ enabled: draftEnabled.value, amount });
    if (ok) {
      // 保存成功 → 该金额进入输入框上方的历史下拉
      rememberAmount(amount);
      draftAmount.value = amount; // 空值保存后显示 0，不留空
      enabledDirty = false;
      amountDirty = false;
      api.message.success('全局配置已保存');
    } else {
      api.message.error('保存失败');
    }
  } catch (e) {
    api.message.error(e instanceof Error && e.message ? e.message : '保存失败');
  } finally {
    saving.value = false;
  }
}

loadAmountOptions();
onMounted(() => {
  const saved = readSavedPos();
  if (saved) pos.value = clampPosition(saved.left, saved.top);
  window.addEventListener('resize', onWindowResize);

  // 连接可能还没就绪导致房间数为 0，补拉一次
  if (props.roomCount === 0) props.onRefresh?.();
});
onUnmounted(() => {
  clearBlurTimer();
  window.removeEventListener('resize', onWindowResize);
});
</script>

<style scoped lang="scss">
$gold: #d8b25f;
$gold-hi: #f6e3b6;
$muted: #9b8f79;
$faint: #6d6454;
$text: #efe6d4;

// ---------- 面板（常显、可拖拽、单行） ----------
// 壳用冷蓝黑玻璃（主界面是暖金黑）：色温对比 + 亮边框 + 重投影，
// 悬浮在主界面任何位置都一眼可辨，不会再“融进背景”
.qk-panel {
  position: fixed;
  right: 14px;
  bottom: 84px;
  z-index: 2000;
  width: min(326px, calc(100vw - 20px));
  padding: 6px 8px 8px;
  cursor: grab;
  touch-action: none;
  border-radius: 10px;
  background-image:
    radial-gradient(90% 130% at 50% -30%, rgba(56, 189, 248, 0.12), transparent 70%),
    linear-gradient(165deg, rgba(15, 23, 42, 0.97), rgba(2, 6, 23, 0.97));
  border: 1px solid rgba(56, 189, 248, 0.45);
  box-shadow:
    0 16px 44px -12px rgba(0, 0, 0, 0.92),
    0 0 0 1px rgba(56, 189, 248, 0.12),
    inset 0 1px 0 rgba(148, 197, 253, 0.12);
  backdrop-filter: blur(10px) saturate(1.2);

  &.dragging {
    cursor: grabbing;
    user-select: none;
    box-shadow:
      0 20px 52px -12px rgba(0, 0, 0, 0.94),
      0 0 0 1px rgba(56, 189, 248, 0.2),
      inset 0 1px 0 rgba(148, 197, 253, 0.14);
  }
}

// 顶部拖拽条：横向整宽，随便点哪儿都能拖
.qk-dragbar {
  height: 16px;
  margin: -2px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-touch-callout: none;

  // 中间一根短横条，作为可拖的视觉提示
  &::before {
    content: '';
    width: 34px;
    height: 3px;
    border-radius: 999px;
    background: rgba(216, 178, 95, 0.35);
    transition: width 0.15s, background 0.15s;
  }

  &:hover::before {
    width: 48px;
    background: rgba(240, 208, 138, 0.7);
  }

  &:active { cursor: grabbing; }
}

.qk-panel.dragging .qk-dragbar::before {
  width: 48px;
  background: rgba(240, 208, 138, 0.85);
}

.qk-bar {
  display: flex;
  align-items: center;
  gap: 6px;
}

// 无可用房间时的锁定提示
.qk-empty {
  padding: 2px 4px 4px;
  text-align: center;
  font-size: 11.5px;
  letter-spacing: 0.3px;
  color: $muted;
}

.qk-kill,
.qk-amount {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.qk-kill {
  flex: none;
}

.qk-amount {
  flex: 1;
}

.qk-label {
  flex: none;
  font-size: 11.5px;
  font-weight: 700;
  color: $text;
  letter-spacing: 0.3px;
}

.qk-input-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.qk-input {
  width: 100%;
}

// ---------- 历史金额下拉 ----------
.qk-drop {
  position: absolute;
  right: 0;
  bottom: calc(100% + 5px);
  z-index: 10;
  width: 224px;
  max-width: calc(100vw - 24px);
  max-height: 132px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px;
  touch-action: pan-y;
  border-radius: 8px;
  background: #131a2a;
  border: 1px solid rgba(56, 189, 248, 0.35);
  box-shadow:
    0 18px 36px -18px #000,
    0 0 28px -18px rgba(56, 189, 248, 0.45);

  // 面板贴近屏幕顶部时向下展开
  &.below {
    bottom: auto;
    top: calc(100% + 5px);
  }

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.25);
    border-radius: 999px;
  }
}

.qk-drop-item {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-family: var(--kp-mono, monospace);
  color: #cbbda2;
  background: rgba(216, 178, 95, 0.08);
  border: 1px solid rgba(216, 178, 95, 0.18);
  transition: color 0.15s, background 0.15s, border-color 0.15s;

  &:hover {
    color: #ffe9bf;
    background: rgba(216, 178, 95, 0.18);
    border-color: rgba(240, 208, 138, 0.45);
  }

  &.active {
    color: #ffe9bf;
    background: rgba(216, 178, 95, 0.2);
    border-color: rgba(240, 208, 138, 0.55);
  }
}

// ---------- 保存按钮的状态标识（与首页“保存设置 / 已保存”一致） ----------
.qk-bar :deep(.n-button) {
  padding-left: 10px;
  padding-right: 10px;
}

.qk-save-inner {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.qk-save-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: qkSavePulse 1.2s ease-in-out infinite;
}

.qk-check {
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
}

@keyframes qkSavePulse {
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

@media (max-width: 480px) {
  .qk-panel {
    right: 10px;
    bottom: 78px;
    width: min(326px, calc(100vw - 16px));
  }
}
</style>
